import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Background,
  Controls,
  MiniMap,
  ConnectionMode,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeDragHandler,
  type OnEdgesChange,
  type OnNodesChange,
  type ReactFlowInstance,
} from "reactflow";
import ReactFlow from "reactflow";

import "reactflow/dist/style.css";

import CBCNode from "./CBCNode";
import type {
  CanvasPosition,
  CBCNodeData,
  CBCRelation,
  EnsembleCBC,
} from "../../types/ensemble.types";
import EditableEdge from "./EditableEdge";
import "./editableEdge.css";

const nodeTypes = {
  cbcNode: CBCNode,
};

// Minimap dot colour per concept type — mirrors the node accent hues so the
// minimap reads as a scaled-down version of the canvas.
const MINIMAP_TYPE_COLORS: Record<string, string> = {
  person: "#0ea5e9",
  event: "#f59e0b",
  thing: "#10b981",
  place: "#8b5cf6",
};
const minimapNodeColor = (node: Node): string =>
  MINIMAP_TYPE_COLORS[
    (node.data as CBCNodeData | undefined)?.cbc?.ensemble_model_cbc_type ?? ""
  ] ?? "#94a3b8";

const EDGE_TYPE: Edge["type"] = "editableEdge";
const EDGE_STYLE: Edge["style"] = {
  stroke: "#64748b",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  opacity: 0.8,
};
const edgeTypes = {
  editableEdge: EditableEdge,
};

type EdgeMeta = {
  relationId: number;
  sourceId: number;
  targetId: number;
  multiIndex?: number;
  multiCount?: number;
};

const withParallelEdgeMeta = (edges: Edge[]): Edge[] => {
  const grouped = new Map<string, Edge[]>();

  edges.forEach((edge) => {
    const key = `${edge.source}->${edge.target}`;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(edge);
  });

  return edges.map((edge) => {
    const key = `${edge.source}->${edge.target}`;
    const parallels = grouped.get(key) ?? [];
    const index = parallels.findIndex((candidate) => candidate.id === edge.id);
    const multiCount = parallels.length;
    const meta = (edge.data?.meta as EdgeMeta | undefined) ?? {
      relationId: Number.NaN,
      sourceId: Number(edge.source),
      targetId: Number(edge.target),
    };

    return {
      ...edge,
      data: {
        ...edge.data,
        meta: {
          ...meta,
          multiIndex: index < 0 ? 0 : index,
          multiCount,
        },
      },
    };
  });
};

export interface EnsembleCanvasProps {
  cbcs: EnsembleCBC[];
  relations: CBCRelation[];
  selectedCBCId: number | null;
  onCBCSelect: (id: number) => void;
  onEdgeLabelChange?: (
    relationId: number,
    details: string,
    meta: { sourceId: number; targetId: number }
  ) => void;
  onNodesPersist?: (nodes: Node<CBCNodeData>[]) => void;
  // Fired when the user finishes dragging a node, with that node's final
  // position. Used to persist canvas layout to the backend on drag-end.
  onNodePositionCommit?: (id: number, position: CanvasPosition) => void;
  onRelationCreate?: (
    sourceId: number,
    targetId: number
  ) => Promise<void> | void;
  className?: string;
}

export const EnsembleCanvas: React.FC<EnsembleCanvasProps> = ({
  cbcs,
  relations,
  selectedCBCId,
  onCBCSelect,
  onEdgeLabelChange,
  onNodesPersist,
  onNodePositionCommit,
  onRelationCreate,
  className,
}) => {
  const [flowInstance, setFlowInstance] =
    useState<ReactFlowInstance | null>(null);

  // Node identity/position comes from the fetched CBCs only. Selection is applied
  // separately below — folding `selectedCBCId` in here would rebuild every node on
  // each click and reset live (dragged) positions back to the last-fetched
  // canvas_position.
  const initialNodes = useMemo<Node<CBCNodeData>[]>(() => {
    return cbcs.map((cbc) => ({
      id: cbc.ensemble_model_cbc_id.toString(),
      type: "cbcNode",
      position: cbc.canvas_position,
      selectable: false,
      data: {
        cbc,
        isSelected: false,
        onSelect: onCBCSelect,
      },
    }));
  }, [cbcs, onCBCSelect]);

  const initialEdges = useMemo<Edge[]>(() => {
    const mapped = relations.map((relation) => ({
      id: relation.ensemble_model_cbc_relation_id.toString(),
      source: relation.src_ensemble_model_cbc_id.toString(),
      target: relation.tgt_ensemble_model_cbc_id.toString(),
      animated: false,
      type: EDGE_TYPE,
      style: EDGE_STYLE,
      data: {
        label: relation.relation_description ?? "",
        meta: {
          relationId: relation.ensemble_model_cbc_relation_id,
          sourceId: relation.src_ensemble_model_cbc_id,
          targetId: relation.tgt_ensemble_model_cbc_id,
        } as EdgeMeta,
        onLabelChange: onEdgeLabelChange,
      },
    }));
    return withParallelEdgeMeta(mapped);
  }, [relations, onEdgeLabelChange]);

  const [nodes, setNodes, onNodesChangeInternal] =
    useNodesState<CBCNodeData>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  // Apply selection as a surgical data-only update so the node keeps whatever
  // position it currently has on the canvas (including an un-saved drag).
  // Depends on `initialNodes` as well as the selection itself: the effect above
  // rebuilds every node with `isSelected: false` whenever the fetched CBCs
  // change, and `selectedCBCId` usually survives a refetch unchanged, so
  // without that dependency the selection would silently be dropped.
  useEffect(() => {
    setNodes((current) => {
      let changed = false;
      const next = current.map((node) => {
        const isSelected = Number(node.id) === selectedCBCId;
        if (node.data.isSelected === isSelected) {
          return node;
        }
        changed = true;
        return { ...node, data: { ...node.data, isSelected } };
      });
      return changed ? next : current;
    });
  }, [selectedCBCId, initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  const handleNodesChange: OnNodesChange = useCallback(
    (changes) => {
      onNodesChangeInternal(changes);
    },
    [onNodesChangeInternal]
  );

  useEffect(() => {
    if (onNodesPersist) {
      onNodesPersist(nodes);
    }
  }, [nodes, onNodesPersist]);

  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes);
      setEdges((existing) => withParallelEdgeMeta(existing));
    },
    [onEdgesChange, setEdges]
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      const { source, target } = connection;
      if (!source || !target) {
        return;
      }

      const sourceId = Number(source);
      const targetId = Number(target);

      if (
        !Number.isFinite(sourceId) ||
        !Number.isFinite(targetId) ||
        sourceId === targetId
      ) {
        return;
      }

      const newEdge: Edge = {
        id: `temp-${source}-${target}-${Date.now()}`,
        source,
        target,
        animated: false,
        type: EDGE_TYPE,
        style: EDGE_STYLE,
        data: {
          label: "",
          meta: {
            relationId: Number.NaN,
            sourceId,
            targetId,
          },
          onLabelChange: onEdgeLabelChange,
        },
      };

      setEdges((existing) => withParallelEdgeMeta([...existing, newEdge]));

      if (onRelationCreate) {
        Promise.resolve(onRelationCreate(sourceId, targetId)).catch(() => {
          setEdges((existing) =>
            existing.filter((edge) => edge.id !== newEdge.id)
          );
        });
      }
    },
    [onRelationCreate, setEdges]
  );

  const handleNodeDragStop: NodeDragHandler = useCallback(
    (_event, node) => {
      if (!onNodePositionCommit) {
        return;
      }
      const nodeId = Number(node.id);
      if (!Number.isFinite(nodeId)) {
        return;
      }
      onNodePositionCommit(nodeId, {
        x: Math.round(node.position?.x ?? 0),
        y: Math.round(node.position?.y ?? 0),
      });
    },
    [onNodePositionCommit]
  );

  useEffect(() => {
    if (!flowInstance) {
      return undefined;
    }

    if (cbcs.length === 0) {
      flowInstance.setViewport({ x: 0, y: 0, zoom: 0.75 });
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      flowInstance.fitView({
        padding: 0.6,
        includeHiddenNodes: true,
        duration: 400,
      });
    }, 80);

    return () => window.clearTimeout(timeout);
  }, [cbcs, flowInstance]);

  return (
    <div
      className={`tw-relative tw-h-full tw-w-full tw-rounded-[1.75rem] ${className ?? ""}`}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onNodeDragStop={handleNodeDragStop}
        onConnect={handleConnect}
        connectionMode={ConnectionMode.Loose}
        defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
        minZoom={0.2}
        maxZoom={2.5}
        onInit={setFlowInstance}
        className="tw-rounded-[1.75rem]"
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        style={{ width: "100%", height: "100%" }}
      >
        <Background color="#e2e8f0" gap={24} />
        <Controls className="tw-rounded-xl tw-bg-white tw-shadow-lg" />
        <MiniMap
          className="tw-rounded-xl tw-bg-white tw-text-slate-600 tw-shadow-lg"
          nodeColor={minimapNodeColor}
        />
      </ReactFlow>
    </div>
  );
};

export default EnsembleCanvas;
